import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";

export default function PriceOptimization() {
    return (
        <div className="space-y-6">
            <h2 className="text-3xl font-bold tracking-tight">
                Price Optimization Settings
            </h2>
            <div className="grid gap-6">
                <Card>
                    <CardHeader>
                        <CardTitle>Market Management</CardTitle>
                        <CardDescription>
                            Managing optimization across different markets
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            Optifi allows you to optimize prices across multiple
                            markets, each with its own currency and pricing
                            rules. You can enable or disable optimization for
                            specific markets and set custom pricing rules for
                            each one.
                        </p>
                        <div className="rounded-md bg-muted p-4">
                            <p className="text-sm">Market features:</p>
                            <ul className="mt-2 list-disc pl-4 text-sm space-y-2">
                                <li>
                                    Enable/disable optimization for individual
                                    markets
                                </li>
                                <li>
                                    Set market-specific price ranges and costs
                                </li>
                                <li>
                                    Automatic currency conversion between
                                    markets
                                </li>
                                <li>
                                    Visual indicators for market-specific
                                    overrides
                                </li>
                            </ul>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>Price Range Settings</CardTitle>
                        <CardDescription>
                            Setting minimum and maximum prices
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            Define the boundaries within which your product
                            prices can be optimized. These limits can be set
                            globally for all markets or customized for specific
                            markets.
                        </p>
                        <div className="rounded-md bg-muted p-4">
                            <p className="text-sm">Price range options:</p>
                            <ul className="mt-2 list-disc pl-4 text-sm space-y-2">
                                <li>Set minimum and maximum price limits</li>
                                <li>Different ranges for different markets</li>
                                <li>
                                    Automatic currency conversion for global
                                    settings
                                </li>
                                <li>Market-specific overrides when needed</li>
                            </ul>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>Cost and Markup</CardTitle>
                        <CardDescription>
                            Managing product costs and margins
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            Set your product costs and desired markup to ensure
                            optimal profit margins. Costs can be imported from
                            Shopify or set manually, and can be customized per
                            market.
                        </p>
                        <div className="rounded-md bg-muted p-4">
                            <p className="text-sm">Cost management features:</p>
                            <ul className="mt-2 list-disc pl-4 text-sm space-y-2">
                                <li>Import costs from Shopify</li>
                                <li>Manual cost override options</li>
                                <li>Market-specific cost adjustments</li>
                                <li>Automatic currency conversion for costs</li>
                            </ul>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>Price Presentation</CardTitle>
                        <CardDescription>
                            Customizing how prices are displayed
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            Control how your prices are rounded and displayed in
                            different markets. This helps maintain consistent
                            pricing patterns across your store.
                        </p>
                        <div className="rounded-md bg-muted p-4">
                            <p className="text-sm">Presentation options:</p>
                            <ul className="mt-2 list-disc pl-4 text-sm space-y-2">
                                <li>Customize price rounding rules</li>
                                <li>Market-specific presentation settings</li>
                                <li>Maintain consistent price endings</li>
                                <li>
                                    Override global presentation rules per
                                    market
                                </li>
                            </ul>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>Optimization Metrics</CardTitle>
                        <CardDescription>
                            Choosing your optimization strategy
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            Select the metric that Optifi should optimize for:
                            Daily Gross Profit (DGP) or Price Per View (PPV).
                            This determines how the system calculates optimal
                            prices.
                        </p>
                        <div className="rounded-md bg-muted p-4">
                            <p className="text-sm">Available metrics:</p>
                            <ul className="mt-2 list-disc pl-4 text-sm space-y-2">
                                <li>
                                    Daily Gross Profit (DGP) - Optimizes for
                                    maximum daily profit
                                </li>
                                <li>
                                    Price Per View (PPV) - Optimizes based on
                                    product views
                                </li>
                                <li>
                                    Metric selection affects all enabled markets
                                </li>
                                <li>Can be changed at any time</li>
                            </ul>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
